import { type ProgressVariant } from 'design-system/progress/progress';
import { calculatePercentage, formatBytes, formatCount } from 'lib/number';

type QuotaMetric =
  | 'bytes_processed'
  | 'pages_crawled'
  | 'pdf_pages_processed'
  | 'questions'
  | 'retrieval_calls'
  | 'platform_agent_requests';

type QuotaPeriod = 'minute' | 'hour' | 'day' | 'month' | 'never';

export interface TeamQuotaUsage {
  metric: QuotaMetric;
  unit: string;
  description: string;
  period: QuotaPeriod;
  enforcement: 'hard' | 'soft';
  limit: number;
  usage: number;
}

export interface TeamQuotaUsageResponse {
  quotas: TeamQuotaUsage[];
}

// Filters to hard quotas only: the UI does not display soft quotas right now
// because they do not do anything (requests still go through).
export const toHardQuotas = (
  quotas: TeamQuotaUsage[] | undefined,
): TeamQuotaUsage[] =>
  (quotas ?? []).filter((quota) => quota.enforcement === 'hard');

export type QuotaUsageStatus = 'ok' | 'near_limit' | 'over_limit';

export const QUOTA_BAR_VARIANTS: Record<QuotaUsageStatus, ProgressVariant> = {
  ok: 'brand',
  near_limit: 'warning',
  over_limit: 'destructive',
};

export interface QuotaUsageRow {
  metric: QuotaMetric;
  label: string;
  description: string;
  usageLabel: string;
  limitLabel: string;
  percentage: number;
  status: QuotaUsageStatus;
  statusLabel: string | null;
  periodLabel: string;
}

const METRIC_LABELS: Record<QuotaMetric, string> = {
  bytes_processed: 'Data processed',
  pages_crawled: 'Pages crawled',
  pdf_pages_processed: 'PDF pages processed',
  questions: 'Questions',
  retrieval_calls: 'Retrieval queries',
  platform_agent_requests: 'Kapa platform agent requests',
};

const RESET_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

// Periods are fixed UTC windows; "never" means a running total.
const getPeriodLabel = (period: QuotaPeriod, now: Date) => {
  switch (period) {
    case 'minute':
      return 'Resets every minute';
    case 'hour':
      return 'Resets every hour';
    case 'day': {
      const next = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1),
      );
      return `Resets on ${RESET_DATE_FORMAT.format(next)} at 00:00 UTC`;
    }
    case 'month': {
      const next = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1),
      );
      return `Resets on ${RESET_DATE_FORMAT.format(next)} at 00:00 UTC`;
    }
    default:
      return 'Lifetime total, does not reset';
  }
};

const NEAR_LIMIT_PERCENTAGE = 80;

const getStatusLabel = (status: QuotaUsageStatus, quota: TeamQuotaUsage) => {
  if (status === 'near_limit') {
    return 'Near limit';
  }
  if (status === 'over_limit') {
    if (quota.enforcement === 'soft') {
      return 'Over limit, requests still go through';
    }
    return quota.period === 'never'
      ? 'Limit reached, requests are refused'
      : 'Limit reached, requests are refused until reset';
  }
  return null;
};

const formatValue = (value: number, unit: string) =>
  unit === 'bytes' ? formatBytes(value) : formatCount(value);

// Derived from the raw ratio, not the rounded percentage: at 9,995 of 10,000
// the display rounds to 100 %, but the limit is not reached and hard requests
// still go through, so the card must not turn red.
const getStatus = (usage: number, limit: number): QuotaUsageStatus => {
  if (limit <= 0) {
    return 'ok';
  }
  const ratio = (usage / limit) * 100;
  if (ratio >= 100) {
    return 'over_limit';
  }
  if (ratio >= NEAR_LIMIT_PERCENTAGE) {
    return 'near_limit';
  }
  return 'ok';
};

// What the platform bar lists, in a fixed order independent of the response.
const COMPACT_METRICS: QuotaMetric[] = [
  'questions',
  'retrieval_calls',
  'bytes_processed',
  'pdf_pages_processed',
  'pages_crawled',
];

export const toQuotaUsageRow = (
  quota: TeamQuotaUsage,
  now: Date,
): QuotaUsageRow => {
  const percentage = calculatePercentage(quota.usage, quota.limit);
  const status = getStatus(quota.usage, quota.limit);
  const limitLabel = formatValue(quota.limit, quota.unit);
  return {
    metric: quota.metric,
    // Fall back to the raw key so an unmapped metric still renders instead of
    // throwing where the label is used.
    label: METRIC_LABELS[quota.metric] ?? quota.metric,
    description: quota.description,
    usageLabel: formatValue(quota.usage, quota.unit),
    limitLabel:
      quota.unit === 'bytes' ? limitLabel : `${limitLabel} ${quota.unit}`,
    percentage,
    status,
    statusLabel: getStatusLabel(status, quota),
    periodLabel: getPeriodLabel(quota.period, now),
  };
};

export const toCompactQuotaUsageRows = (
  quotas: TeamQuotaUsage[],
  now: Date,
): QuotaUsageRow[] =>
  COMPACT_METRICS.flatMap((metric) => {
    const quota = quotas.find((item) => item.metric === metric);
    return quota ? [toQuotaUsageRow(quota, now)] : [];
  });
